/**
 * In-memory cache of the credentials the native layer needs to post live
 * en-route pings (Sessions API base URL + a short-lived Cognito ID token).
 *
 * WHY (2026-10-19 field test, build 127): arming native tracking waited for
 * async JS (token fetch + the server's journey-start response + a React
 * effect), and JS stops running the instant Maps / Android Auto takes the
 * screen — 107 s with no foreground service and no pings. The travel TAP has
 * to hand native everything in the same synchronous JS turn, so the token is
 * pre-fetched while the provider is on the "ready to go" screen and PEEKED
 * (synchronously) at the tap.
 *
 * MEMORY ONLY — a module variable, never persisted, never logged, never a
 * refresh token. Cleared on sign-out / account switch
 * (`resetActiveSessionForSignOut`) and whenever it would be stale.
 * Dependency-free so the stores can import it without pulling in Amplify.
 */

export interface CachedNativeLivePingCredentials {
  apiBaseUrl: string
  /** Cognito ID token — never a refresh token. */
  idToken: string
  /** Token `exp`, epoch ms. */
  expiresAt: number
}

/** The tap path accepts a cached token with at least this long left (native refuses < 60 s). */
export const NATIVE_TAP_TOKEN_MIN_REMAINING_MS = 10 * 60_000

let cached: CachedNativeLivePingCredentials | null = null
/**
 * Bumped by every {@link clearNativeLivePingCredentialCache} (sign-out /
 * account switch). A fetch captures it before awaiting and only writes (or
 * hands out) its token when it is unchanged, so an in-flight fetch can never
 * refill the cache — or arm native — after the user signed out.
 */
let epoch = 0

export function getNativeLivePingCredentialEpoch(): number {
  return epoch
}

/** Store `creds` only if no sign-out happened since `fetchEpoch` was captured. Returns whether it was stored. */
export function cacheNativeLivePingCredentials(
  creds: CachedNativeLivePingCredentials,
  fetchEpoch: number = epoch,
): boolean {
  if (fetchEpoch !== epoch) return false
  cached = creds
  return true
}

/**
 * Synchronous read for the travel tap. Null when nothing is cached or the
 * cached token has less than `minRemainingMs` left.
 */
export function peekNativeLivePingCredentials(
  now: number = Date.now(),
  minRemainingMs: number = NATIVE_TAP_TOKEN_MIN_REMAINING_MS,
): CachedNativeLivePingCredentials | null {
  if (!cached) return null
  if (cached.expiresAt - now < minRemainingMs) return null
  return cached
}

export function clearNativeLivePingCredentialCache(): void {
  epoch += 1
  cached = null
}
